import React, { useMemo, useState } from 'react';
import { XIcon, UserPlusIcon, SearchIcon, CheckIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ScreenProps } from '../../types/ptp';
import { PrimaryButton } from '../ui/PrimaryButton';
import { SecondaryButton } from '../ui/SecondaryButton';
import { useI18n } from '../../config/i18n';
interface Worker {
  name: string;
  role: string;
  responsibility: string;
  onSite: boolean;
}
// Full company roster — workers available to add
const ALL_WORKERS: {
  name: string;
  role: string;
}[] = [{
  name: 'Mike Torres',
  role: 'Foreman'
}, {
  name: 'Dan Kowalski',
  role: 'Crane Operator'
}, {
  name: 'Luis Reyes',
  role: 'Rigger'
}, {
  name: 'Jake Brennan',
  role: 'Ironworker'
}, {
  name: 'Carlos Medina',
  role: 'Laborer'
}, {
  name: 'Tony Russo',
  role: 'Rigger'
}, {
  name: 'Ray Chen',
  role: 'Crane Operator'
}, {
  name: 'Sam Okafor',
  role: 'Electrician'
}, {
  name: 'Eddie Varga',
  role: 'Welder'
}, {
  name: 'Marco Silva',
  role: 'Ironworker'
}, {
  name: 'Pete Nowak',
  role: 'Laborer'
}, {
  name: 'Omar Hassan',
  role: 'Safety Officer'
}, {
  name: 'Hector Diaz',
  role: 'Scaffolder'
}, {
  name: 'Vince Moretti',
  role: 'Pipefitter'
}, {
  name: 'Greg Palmer',
  role: 'Carpenter'
}, {
  name: 'Nate Schultz',
  role: 'Equipment Operator'
}, {
  name: 'Frank Deluca',
  role: 'Surveyor'
}, {
  name: 'Ricky Tran',
  role: 'Laborer'
}];
export function CrewAssignmentScreen({
  data,
  updateData,
  onNext
}: ScreenProps) {
  const {
    t
  } = useI18n();
  const [workers, setWorkers] = useState<Worker[]>(() => data.crewRoles.length > 0 ? data.crewRoles.map(cr => ({
    name: cr.name,
    role: cr.role,
    responsibility: cr.responsibility,
    onSite: true
  })) : []);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const handleRemove = (name: string) => {
    const updated = workers.filter(w => w.name !== name);
    setWorkers(updated);
    updateData({
      crewSize: updated.length,
      crewRoles: updated.map(w => ({
        name: w.name,
        role: w.role,
        responsibility: w.responsibility
      }))
    });
  };
  const handleAddWorker = (rosterWorker: {
    name: string;
    role: string;
  }) => {
    if (workers.some(w => w.name === rosterWorker.name)) return;
    const newWorker: Worker = {
      name: rosterWorker.name,
      role: rosterWorker.role,
      responsibility: '',
      onSite: true
    };
    const updated = [...workers, newWorker];
    setWorkers(updated);
    updateData({
      crewSize: updated.length,
      crewRoles: updated.map(w => ({
        name: w.name,
        role: w.role,
        responsibility: w.responsibility
      }))
    });
  };
  const assignedNames = useMemo(() => new Set(workers.map(w => w.name)), [workers]);
  const filteredRoster = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    return ALL_WORKERS.filter(w => {
      if (!q) return true;
      return w.name.toLowerCase().includes(q) || w.role.toLowerCase().includes(q);
    });
  }, [searchQuery]);
  return <div className="flex flex-col h-full relative" data-id="element-198">
      <div className="mb-5" data-id="element-199">
        <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-200">
          {t('crew.title')}
        </h2>
        <p className="text-gray-500 text-sm" data-id="element-201">{t('crew.subtitle')}</p>
      </div>

      <div className="flex-1 overflow-y-auto pb-6 -mx-2 px-2 space-y-4" data-id="element-202">
        <div className="bg-white border border-gray-200 rounded-xl px-4 py-3 flex items-center justify-between" data-id="element-203">
          <span className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-204">
            {t('crew.size')}
          </span>
          <span className="text-gray-900 font-bold text-lg" data-id="element-205">
            {workers.length}
          </span>
        </div>

        <div className="space-y-2.5" data-id="element-206">
          <AnimatePresence data-id="element-207">
            {workers.map((worker, index) => <motion.div key={worker.name} layout initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            x: -60,
            height: 0,
            marginBottom: 0
          }} transition={{
            delay: index * 0.05,
            duration: 0.25
          }} className="bg-white border border-gray-200 rounded-xl overflow-hidden relative" data-id="element-208">
                <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${worker.role === 'Foreman' ? 'bg-yellow-500' : 'bg-gray-300'}`} data-id="element-209" />
                <div className="p-4 pl-5 flex items-center gap-3" data-id="element-210">
                  <div className="flex-1 min-w-0" data-id="element-211">
                    <div className="flex items-center gap-2 mb-0.5" data-id="element-212">
                      <h4 className="text-gray-900 font-semibold text-base" data-id="element-213">
                        {worker.name}
                      </h4>
                      <span className="text-gray-400 text-xs" data-id="element-214">·</span>
                      <span className="text-gray-500 text-sm" data-id="element-215">
                        {worker.role}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-1.5" data-id="element-216">
                      <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" data-id="element-217" />
                      <span className="text-green-600 text-[10px] font-bold uppercase tracking-wider" data-id="element-218">
                        On site
                      </span>
                    </div>
                  </div>
                  {worker.role !== 'Foreman' && <button onClick={() => handleRemove(worker.name)} className="p-2 rounded-lg bg-gray-100 border border-gray-200 text-red-400 hover:text-red-500 hover:bg-red-50 hover:border-red-200 transition-colors flex-shrink-0" data-id="element-219">
                      <XIcon className="w-4 h-4" data-id="element-220" />
                    </button>}
                </div>
              </motion.div>)}
          </AnimatePresence>
        </div>
      </div>

      <div className="sticky bottom-0 z-10 pt-3 pb-2 space-y-3 bg-gray-50" data-id="element-221">
        <PrimaryButton onClick={onNext} data-id="element-222">{t('continue')}</PrimaryButton>
        <SecondaryButton onClick={() => {
        setShowAddModal(true);
        setSearchQuery('');
      }} icon={<UserPlusIcon className="w-5 h-5" data-id="element-224" />} data-id="element-223">
          {t('add')}
        </SecondaryButton>
      </div>

      {/* Add Worker Modal */}
      <AnimatePresence data-id="element-225">
        {showAddModal && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowAddModal(false)} className="absolute inset-0 bg-black/30 backdrop-blur-sm z-40" data-id="element-226" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="absolute inset-x-0 bottom-0 top-12 bg-white border-t border-gray-200 rounded-t-2xl z-50 flex flex-col overflow-hidden" data-id="element-227">
              {/* Header */}
              <div className="px-5 pt-4 pb-3 flex items-center justify-between border-b border-gray-200" data-id="element-228">
                <h3 className="text-lg font-bold text-gray-900" data-id="element-229">
                  {t('crew.addWorker')}
                </h3>
                <button onClick={() => setShowAddModal(false)} className="p-2 text-gray-400 hover:text-gray-900 transition-colors rounded-full hover:bg-gray-100" data-id="element-230">
                  <XIcon className="w-5 h-5" data-id="element-231" />
                </button>
              </div>

              {/* Search */}
              <div className="px-5 py-3" data-id="element-232">
                <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5" data-id="element-233">
                  <SearchIcon className="w-4 h-4 text-gray-400 flex-shrink-0" data-id="element-234" />
                  <input autoFocus value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder={t('crew.searchPlaceholder')} className="flex-1 bg-transparent text-gray-900 text-sm outline-none placeholder-gray-400" data-id="element-235" />
                  {searchQuery && <button onClick={() => setSearchQuery('')} className="text-gray-400 hover:text-gray-700" data-id="element-236">
                      <XIcon className="w-3.5 h-3.5" data-id="element-237" />
                    </button>}
                </div>
              </div>

              {/* Worker List */}
              <div className="flex-1 overflow-y-auto px-5 pb-6" data-id="element-238">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3" data-id="element-239">
                  {filteredRoster.length} worker
                  {filteredRoster.length !== 1 ? 's' : ''}
                </p>
                <div className="space-y-1.5" data-id="element-240">
                  {filteredRoster.map(rosterWorker => {
                const isAssigned = assignedNames.has(rosterWorker.name);
                return <motion.button key={rosterWorker.name} whileTap={!isAssigned ? {
                  scale: 0.98
                } : undefined} onClick={() => !isAssigned && handleAddWorker(rosterWorker)} disabled={isAssigned} className={`w-full rounded-lg p-3 flex items-center gap-3 text-left transition-colors ${isAssigned ? 'bg-emerald-50 border border-emerald-200 cursor-default' : 'bg-gray-100 border border-gray-200 hover:border-gray-300 active:bg-gray-200'}`} data-id="element-241">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${isAssigned ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-200 text-gray-600'}`} data-id="element-242">
                          {rosterWorker.name.charAt(0)}
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-243">
                          <p className={`font-medium text-sm ${isAssigned ? 'text-emerald-700' : 'text-gray-900'}`} data-id="element-244">
                            {rosterWorker.name}
                          </p>
                          <p className="text-gray-400 text-xs" data-id="element-245">
                            {rosterWorker.role}
                          </p>
                        </div>
                        {isAssigned ? <div className="flex items-center gap-1 flex-shrink-0" data-id="element-246">
                            <CheckIcon className="w-4 h-4 text-emerald-500" data-id="element-247" />
                            <span className="text-emerald-600 text-[10px] font-bold uppercase" data-id="element-248">
                              {t('crew.added')}
                            </span>
                          </div> : <div className="w-7 h-7 rounded-full bg-gray-200 border border-gray-300 flex items-center justify-center flex-shrink-0" data-id="element-249">
                            <UserPlusIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-250" />
                          </div>}
                      </motion.button>;
              })}

                  {filteredRoster.length === 0 && <div className="text-center py-8" data-id="element-251">
                      <p className="text-gray-400 text-sm" data-id="element-252">
                        {t('crew.noMatch')} "{searchQuery}"
                      </p>
                    </div>}
                </div>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </div>;
}